import {
  type CSSProperties,
  type KeyboardEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import './season-strip.css'

export type SeasonTick = {
  /** Stable per tick — the day it stands for. */
  key: string
  /** Of the tallest day, 0 to 1. */
  share: number
  isToday: boolean
  /** The day, as the readout names it — "Sep 10". */
  label: string
  /** What came in that day, as the readout states it. */
  detail: string
  /** A label under this tick, where a stretch begins — "Aug 13", "Sep". */
  mark?: string
}

/**
 * The days a campaign has collected over, one tick each, as tall as what came
 * in — the opening day on the leading edge and today on the trailing one.
 *
 * An appeal is a window, and the question a treasurer brings to it is whether
 * money is still arriving — which a total cannot answer and a row of ticks
 * answers at a glance. Today's tick is gold: the reader's own position, which
 * is one of the jobs gold holds.
 *
 * **A day keeps a readable width whatever the season's length.** A long season
 * scrolls sideways rather than thinning every day to nothing, and it opens at
 * its trailing edge, because the recent days are the ones being asked about.
 * Marks under the ticks name where a stretch begins, so a scrolled strip still
 * says which weeks it is showing.
 *
 * **What a day took in is read in the line above the strip**, not in a
 * floating label: the day under the pointer, the one a finger tapped or the
 * one the arrow keys reached. A label floating off one tick would be clipped
 * by the strip's own scroll, and would say nothing to a finger, which has no
 * hover. The line keeps its height with nobody pointing: one that appeared on
 * hover would move the days out from under the pointer that summoned it.
 *
 * The days are one stop for the keyboard: Tab reaches the strip, the arrow
 * keys walk it, Home and End jump to its ends.
 *
 * Heights are linear. A square-root scale would lift the quiet days, and make
 * the one large gift look smaller than it is.
 */
export function SeasonStrip({
  ticks,
  note,
  description,
}: {
  /** In order, oldest first. */
  ticks: readonly SeasonTick[]
  /** What the days do NOT cover, where they cover less than the whole span. */
  note?: string
  /** What the strip shows, said in words. */
  description: string
}) {
  const scroller = useRef<HTMLDivElement>(null)
  const count = ticks.length
  const last = count - 1
  const [chosen, setChosen] = useState<number | null>(null)
  const [hovered, setHovered] = useState<number | null>(null)
  /* The day the readout states: the one being pointed at, and today until
     somebody points at one — the strip opens on today, so it opens saying
     what today has brought in. */
  const active = Math.min(last, hovered ?? chosen ?? last)
  const reading = ticks[active]
  /* The keyboard's place, which is the chosen day or today. */
  const stop = Math.min(last, chosen ?? last)

  /* Held at the trailing edge — today — until the reader scrolls it, and
     again whenever the column changes width, which moves where that edge is.
     `scrollLeft` counts from the leading edge in either direction, and the far
     end is its extreme both ways. */
  useLayoutEffect(() => {
    const el = scroller.current
    if (!el || count === 0) return
    let held = true
    const toEnd = () => {
      if (!held) return
      const rtl = getComputedStyle(el).direction === 'rtl'
      el.scrollLeft = rtl ? -el.scrollWidth : el.scrollWidth
    }
    const release = () => {
      held = false
    }
    toEnd()
    const watch = new ResizeObserver(toEnd)
    watch.observe(el)
    el.addEventListener('wheel', release, { passive: true })
    el.addEventListener('touchstart', release, { passive: true })
    el.addEventListener('keydown', release)
    return () => {
      watch.disconnect()
      el.removeEventListener('wheel', release)
      el.removeEventListener('touchstart', release)
      el.removeEventListener('keydown', release)
    }
  }, [count])

  const move = (to: number) => {
    const next = Math.max(0, Math.min(last, to))
    setChosen(next)
    const day =
      scroller.current?.querySelectorAll<HTMLElement>('.season-strip__day')[
        next
      ]
    day?.focus()
    day?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }

  /* Arrow keys follow the writing direction: in a right-to-left page the
     earlier days are on the right. */
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl'
    const back = rtl ? 'ArrowRight' : 'ArrowLeft'
    const on = rtl ? 'ArrowLeft' : 'ArrowRight'
    const to =
      e.key === back
        ? stop - 1
        : e.key === on
          ? stop + 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null
    if (to === null) return
    e.preventDefault()
    move(to)
  }

  if (count === 0 || !reading) return null

  return (
    <figure className="season-strip">
      {/* The day under the pointer speaks above the days, in a line that holds
          its height whether or not anybody is pointing — a line that appeared
          on hover would move the drawing out from under the pointer. */}
      <figcaption className="season-strip__readout">
        <span className="season-strip__when">
          {reading.isToday ? `Today, ${reading.label}` : reading.label}
        </span>
        <span className="season-strip__what">{reading.detail}</span>
        {note ? <span className="season-strip__note">{note}</span> : null}
      </figcaption>
      <div ref={scroller} className="season-strip__scroll">
        {/* A toolbar rather than a group: the arrow keys operate it, and that
            is what a toolbar announces. */}
        <div
          className="season-strip__days"
          role="toolbar"
          aria-label={description}
          onPointerLeave={() => setHovered(null)}
        >
          {ticks.map((tick, i) => (
            <button
              key={tick.key}
              type="button"
              className="season-strip__day"
              data-today={tick.isToday || undefined}
              /* The day the line above is reading, so what it says and which
                 day it came from are one thing. */
              data-active={i === active || undefined}
              tabIndex={i === stop ? 0 : -1}
              aria-label={`${tick.label}: ${tick.detail}`}
              /* One day is being read at a time, so the reading marks where it
                 is. `aria-pressed` would make every other day a toggle
                 announced as off. */
              aria-current={i === active || undefined}
              onPointerEnter={(e) => {
                if (e.pointerType !== 'touch') setHovered(i)
              }}
              onClick={() => setChosen(i === chosen ? null : i)}
              onKeyDown={onKeyDown}
            >
              <span
                className="season-strip__tick"
                data-empty={tick.share === 0 || undefined}
                /* How tall the day is is a fact about this season, so it
                   rides on the element; the look stays in the sheet. */
                style={{ '--share': tick.share } as CSSProperties}
              />
              {tick.mark ? (
                <span className="season-strip__mark" aria-hidden="true">
                  {tick.mark}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </figure>
  )
}
